import { useMemo, useState } from 'react'
import { Check, Search } from 'lucide-react'
import { normalizeTermKey } from '@/domains/taxonomy/normalize-term'
import { classNames } from '@/ui/utils/main'

export interface FilterableOption {
  value: string
  label: string
}

/** Rows visible at once; the list scrolls beyond (each row is 2.25rem high). */
const VISIBLE_ROWS = 10
const ROW_HEIGHT_REM = 2.25

interface FilterableOptionListProps {
  options: FilterableOption[]
  value: string | undefined
  onChange: (value: string) => void
  filterPlaceholder: string
  emptyLabel: string
  'aria-label': string
}

/**
 * Single-choice list with a name filter on top (case and accent insensitive).
 * Shows at most ten rows at a time, the rest is reachable by scrolling.
 */
export function FilterableOptionList({
  options,
  value,
  onChange,
  filterPlaceholder,
  emptyLabel,
  'aria-label': ariaLabel,
}: FilterableOptionListProps) {
  const [filter, setFilter] = useState('')

  const visibleOptions = useMemo(() => {
    const filterKey = normalizeTermKey(filter)
    return options.filter(
      (option) => filterKey.length === 0 || normalizeTermKey(option.label).includes(filterKey),
    )
  }, [options, filter])

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <input
          type="search"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          placeholder={filterPlaceholder}
          aria-label={filterPlaceholder}
          className="w-full pl-9 pr-3 py-1.5 text-sm bg-background border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
        />
      </div>
      <div
        role="listbox"
        aria-label={ariaLabel}
        className="overflow-y-auto rounded-lg border border-border"
        style={{ maxHeight: `${VISIBLE_ROWS * ROW_HEIGHT_REM}rem` }}
      >
        {visibleOptions.length === 0 ? (
          <p className="px-3 py-2 text-sm text-muted-foreground">{emptyLabel}</p>
        ) : (
          visibleOptions.map((option) => {
            const isSelected = option.value === value
            return (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={() => onChange(option.value)}
                className={classNames(
                  'flex w-full items-center gap-2 px-3 text-left text-sm hover:bg-accent hover:text-accent-foreground',
                  isSelected && 'bg-accent text-accent-foreground',
                )}
                style={{ height: `${ROW_HEIGHT_REM}rem` }}
              >
                <Check className={classNames('h-4 w-4 shrink-0', !isSelected && 'invisible')} />
                <span className="truncate">{option.label}</span>
              </button>
            )
          })
        )}
      </div>
    </div>
  )
}
